import {
  flagKeySchema,
  type CaptureOptions,
  type ProductProperties,
  type ProductPropertyValue,
} from '@galapagos/product-analytics';

type PostHogJsonValue =
  | null
  | string
  | number
  | boolean
  | PostHogJsonValue[]
  | { [key: string]: PostHogJsonValue };

export type PostHogProperties = Record<string, PostHogJsonValue>;

function toPostHogJson(value: ProductPropertyValue): PostHogJsonValue {
  if (Array.isArray(value)) return value.map(toPostHogJson);
  if (value !== null && typeof value === 'object') {
    const output: Record<string, PostHogJsonValue> = {};
    for (const [key, entryValue] of Object.entries(value)) {
      output[key] = toPostHogJson(entryValue);
    }
    return output;
  }
  return value;
}

export function postHogPersonProperties(
  properties?: ProductProperties,
): PostHogProperties | undefined {
  if (!properties) return undefined;
  const output: PostHogProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    output[key] = toPostHogJson(value);
  }
  return output;
}

export function postHogProperties(
  properties: ProductProperties,
  options: CaptureOptions = {},
  event?: string,
): PostHogProperties {
  // PostHog's experiments UI recognizes custom exposure through the vendor-native
  // `$feature/<flag-key>` property; the dynamic key is minted only for flag keys
  // that match the registered flag-key grammar.
  const flagKey = flagKeySchema.safeParse(properties.flag_key);
  return {
    ...postHogPersonProperties(properties),
    ...(event?.startsWith('experiment_') &&
    flagKey.success &&
    typeof properties.variant_key === 'string'
      ? { [`$feature/${flagKey.data}`]: properties.variant_key }
      : {}),
    ...(options.processPersonProfile !== undefined
      ? { $process_person_profile: options.processPersonProfile }
      : {}),
    // Vendor clock-skew correction rewrites explicit capture timestamps using
    // sent_at; $ignore_sent_at is PostHog's documented opt-out, required when a
    // server-stamped instant must survive ingestion byte-for-byte.
    ...(options.preserveTimestamp && options.timestamp ? { $ignore_sent_at: true } : {}),
  };
}

export function postHogTimestamp(options: CaptureOptions = {}): Date | undefined {
  return options.timestamp ? new Date(options.timestamp) : undefined;
}

function hash128(value: string): [number, number, number, number] {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    h1 = h2 ^ Math.imul(h1 ^ code, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ code, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ code, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ code, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  return [(h1 ^ h2 ^ h3 ^ h4) >>> 0, (h2 ^ h1) >>> 0, (h3 ^ h1) >>> 0, (h4 ^ h1) >>> 0];
}

/** PostHog deduplicates on its event UUID, so map any bounded envelope id deterministically. */
export function postHogUuid(insertId: string | undefined): string | undefined {
  if (!insertId) return undefined;
  const rawHex = hash128(insertId)
    .map((word) => word.toString(16).padStart(8, '0'))
    .join('');
  const variant = ((Number.parseInt(rawHex.charAt(16), 16) & 0x3) | 0x8).toString(16);
  const value = `${rawHex.slice(0, 12)}5${rawHex.slice(13, 16)}${variant}${rawHex.slice(17)}`;
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}
